import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { logEvent } from '@/lib/analytics';
import { Session } from '@supabase/supabase-js';

export default function AuthCallback() {
  const navigate = useNavigate();
  const callbackUrl = useRef(window.location.href);

  useEffect(() => {
    console.log("📥 AuthCallback: URL is", window.location.href);
    // Log session hydration immediately after redirect
    const getSession = async () => {
      const { data, error } = await supabase.auth.getSession();
    };
    getSession();

    const handleAuthRedirect = async () => {
      try {
        // Check for returnTo in localStorage (set by ShareCarPage or LoginPage)
        const returnTo = localStorage.getItem('auth-return-to');
        if (returnTo) {
          localStorage.removeItem('auth-return-to');
        }

        const { data: { session: existingSession } } = await supabase.auth.getSession();

        // If already signed in, skip exchange
        if (existingSession?.user) {
          // Ensure public.users row exists even when Supabase already hydrated a session
          await upsertUserData(existingSession);
          // If we have a returnTo, use it; otherwise route normally
          if (returnTo) {
            return navigate(returnTo);
          }
          const route = await routeUserAfterAuth(existingSession);
          return navigate(route);
        }

        const url = new URL(callbackUrl.current);
        const code = url.searchParams.get('code');
        if (!code) throw new Error('Missing auth code in URL');

        const { data, error } = await supabase.auth.exchangeCodeForSession(callbackUrl.current);
        console.log("🔁 Session after redirect:", data.session ? 'session received' : 'no session');
        
        if (error) {
          console.error("❌ Error exchanging code for session:", error);
          navigate('/login');
          return;
        }

        if (data.session) {
          console.log("✅ Session exchange successful");
          await upsertUserData(data.session);
          
          // If we have a returnTo, use it; otherwise route normally
          if (returnTo) {
            return navigate(returnTo);
          }
          
          // Fetch user data
          const { data: userData } = await supabase.auth.getUser();
          console.log('🔐 User data fetched:', userData.user ? 'user exists' : 'no user');
          
          // Navigate based on onboarding status
          if (userData.user?.user_metadata?.onboarding_completed) {
            console.log("✅ User onboarding completed, redirecting to home");
            navigate('/home');
          } else {
            console.log("⏳ User needs onboarding, redirecting to tutorial");
            navigate('/onboarding/tutorial');
          }
        } else {
          console.log("❌ No session received after exchange");
          navigate('/login');
        }
      } catch (error) {
        navigate('/auth/error', {
          state: {
            error: error instanceof Error ? error.message : 'Authentication failed'
          }
        });
      }
    };

    handleAuthRedirect();
  }, [navigate]);

  return (
    <div className="h-screen flex flex-col items-center justify-center">
      <LoadingSpinner size="lg" />
      <p className="ml-2 text-muted-foreground">Completing sign in...</p>
    </div>
  );
}

// Merged from auth-utils.ts
async function upsertUserData(session: Session) {
  try {
    const profile = JSON.parse(localStorage.getItem('signup-profile') || '{}');
    
    // Get Google avatar URL from auth metadata (if available)
    const googleAvatarUrl = session.user.user_metadata?.avatar_url || null;

    // For email/password signups: Store display_name in auth metadata (unified approach)
    // For Google OAuth: Pre-fill display_name if missing using Google-provided name fields
    if (profile.display_name && !session.user.user_metadata?.display_name) {
      // Email/password signup - use provided display_name
      await supabase.auth.updateUser({
        data: { display_name: profile.display_name }
      });
    } else if (!session.user.user_metadata?.display_name) {
      // Google OAuth - pre-fill from Google metadata if display_name is missing
      const googleName = session.user.user_metadata?.full_name 
        || session.user.user_metadata?.name
        || (session.user.user_metadata?.given_name && session.user.user_metadata?.family_name
          ? `${session.user.user_metadata.given_name} ${session.user.user_metadata.family_name}`
          : null);
      
      if (googleName) {
        await supabase.auth.updateUser({
          data: { display_name: googleName }
        });
      }
    }

    // Store in public.users (first_name/last_name are deprecated, set to null)
    // NOTE: We use display_name in auth.users.raw_user_meta_data as the primary name field
    const updateData = {
      id: session.user.id,
      email: session.user.email!,
      first_name: null, // Deprecated - use auth.users.raw_user_meta_data.display_name
      last_name: null,  // Deprecated - use auth.users.raw_user_meta_data.display_name
      phone_number: profile.phone_number || null,
      role: 'owner',
      last_seen: new Date().toISOString(),
      // Optionally store Google avatar URL in public.users.avatar_url for reference
      // (custom uploads will use custom_avatar_url instead)
      avatar_url: googleAvatarUrl,
    };

    const { error } = await supabase
      .from('users')
      .upsert(updateData);

    if (error) throw error;

    // Clean up
    localStorage.removeItem('signup-profile');
  } catch (error) {
    console.error('User profile upsert failed:', error);
    throw error;
  }
}

async function waitForUserRow(userId: string, retries = 5, delay = 400): Promise<boolean> {
  for (let i = 0; i < retries; i++) {
    const { data, error } = await supabase
      .from('users')
      .select('onboarding_completed')
      .eq('id', userId)
      .maybeSingle();

    if (data) return !!data.onboarding_completed;
    if (error) throw error; // RLS or fatal error
    await new Promise(res => setTimeout(res, delay));
  }

  throw new Error('User data not found after retries');
}

async function routeUserAfterAuth(session: Session): Promise<string> {
  const onboarded = await waitForUserRow(session.user.id);

  logEvent('user_login', { isNewUser: !onboarded });

  return onboarded ? '/home' : '/onboarding/tutorial';
}
